import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  ClipboardText,
  ShoppingCart,
  Play,
  Truck,
  XCircle,
  Warning,
  CheckCircle,
  PencilSimple,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const STATUS = {
  pending: { label: "In attesa", cls: "bg-[#F3F4F6] text-[#4B5563] border-[#4B5563]/20" },
  planned: { label: "Pianificato", cls: "bg-[#E8F0F8] text-[#457B9D] border-[#457B9D]/20" },
  in_production: { label: "In produzione", cls: "bg-[#FEF5EB] text-[#F4A261] border-[#F4A261]/20" },
  ready: { label: "Pronto", cls: "bg-[#E6F4F1] text-[#2A9D8F] border-[#2A9D8F]/20" },
  shipped: { label: "Spedito", cls: "bg-[#1D3557] text-white border-[#1D3557]" },
  cancelled: { label: "Annullato", cls: "bg-[#FBEDEE] text-[#E63946] border-[#E63946]/20" },
};

export default function Ordini() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty());
  const [detail, setDetail] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  function empty() {
    return {
      customer_name: "",
      customer_email: "",
      customer_address: "",
      delivery_date: "",
      notes: "",
      items: [],
    };
  }

  const load = useCallback(async () => {
    const [o, p] = await Promise.all([
      api.get("/orders"),
      api.get("/products", { params: { type: "prodotto_finito" } }),
    ]);
    setOrders(o.data);
    setProducts(p.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addItem = () =>
    setForm({
      ...form,
      items: [...form.items, { product_id: "", quantity: 1 }],
    });
  const updateItem = (i, key, value) => {
    const next = [...form.items];
    next[i] = { ...next[i], [key]: value };
    setForm({ ...form, items: next });
  };
  const removeItem = (i) =>
    setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name) return toast.error("Inserisci il nome del cliente");
    if (form.items.length === 0) return toast.error("Aggiungi almeno una riga");
    const enriched = [];
    for (let i = 0; i < form.items.length; i++) {
      const it = form.items[i];
      if (!it.product_id) return toast.error(`Riga ${i + 1}: seleziona un prodotto`);
      const p = products.find((x) => x.id === it.product_id);
      if (!p) return toast.error(`Riga ${i + 1}: prodotto non trovato`);
      const q = Number(it.quantity);
      if (!q || q <= 0) return toast.error(`Riga ${i + 1}: quantità > 0`);
      enriched.push({
        product_id: p.id,
        product_name: p.name,
        product_barcode: p.barcode,
        quantity: q,
        unit: p.unit,
      });
    }
    try {
      if (editingId) {
        const { data } = await api.put(`/orders/${editingId}`, {
          ...form,
          items: enriched,
          delivery_date: form.delivery_date || null,
        });
        toast.success(`Ordine ${data.order_number} aggiornato`);
      } else {
        const { data } = await api.post("/orders", {
          ...form,
          items: enriched,
          delivery_date: form.delivery_date || null,
        });
        toast.success(`Ordine ${data.order_number} creato`);
      }
      setCreating(false);
      setEditingId(null);
      setForm(empty());
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEdit = (order) => {
    setForm({
      customer_name: order.customer_name,
      customer_email: order.customer_email || "",
      customer_address: order.customer_address || "",
      delivery_date: order.delivery_date || "",
      notes: order.notes || "",
      items: order.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
    });
    setEditingId(order.id);
    setCreating(true);
  };

  const openDetail = async (order) => {
    setDetail(order);
    setPlan(null);
  };

  const computePlan = async () => {
    if (!detail) return;
    setPlanLoading(true);
    try {
      const { data } = await api.post(`/orders/${detail.id}/plan`);
      setPlan(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPlanLoading(false);
    }
  };

  const executeOrder = async () => {
    if (!detail || !plan) return;
    if (!plan.can_execute) return toast.error("Materiali insufficienti");
    if (!window.confirm(`Avviare la produzione? Saranno creati ${plan.production_plan.length} batch.`)) return;
    setActionLoading(true);
    try {
      const { data } = await api.post(`/orders/${detail.id}/execute`);
      toast.success(`${data.total_steps} batch creati`);
      setDetail(null);
      setPlan(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const shipOrder = async () => {
    if (!detail) return;
    if (!window.confirm("Confermare la spedizione? Lo stock dei PF verrà scaricato.")) return;
    setActionLoading(true);
    try {
      await api.post(`/orders/${detail.id}/ship`);
      toast.success("Ordine spedito");
      setDetail(null);
      setPlan(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const cancelOrder = async () => {
    if (!detail) return;
    if (!window.confirm("Annullare l'ordine?")) return;
    try {
      await api.post(`/orders/${detail.id}/cancel`);
      toast.success("Ordine annullato");
      setDetail(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reopenOrder = async () => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/orders/${detail.id}/reopen`);
      toast.success("Ordine riattivato");
      setDetail(data);
      setPlan(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("it-IT", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="ordini-page">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
            Ordini Clienti · Pianificazione automatica
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Ordini
          </h1>
        </div>
        <button
          onClick={() => { setForm(empty()); setCreating(true); }}
          data-testid="new-order-btn"
          className="bg-[#1D3557] hover:bg-[#2A4B7C] text-white px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors"
        >
          <Plus size={14} weight="bold" /> Nuovo Ordine
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] p-12 text-center">
          <ShoppingCart size={32} weight="duotone" color="#1D3557" className="mx-auto mb-3" />
          <p className="text-sm text-[#4B5563]">Nessun ordine cliente. Crea il primo per iniziare.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto" data-testid="orders-table">
          <table className="w-full">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                <th className="px-6 py-3 font-semibold">Ordine</th>
                <th className="px-6 py-3 font-semibold">Cliente</th>
                <th className="px-6 py-3 font-semibold">Righe</th>
                <th className="px-6 py-3 font-semibold">Stato</th>
                <th className="px-6 py-3 font-semibold">Creato</th>
                <th className="px-6 py-3 font-semibold">Consegna</th>
                <th className="px-6 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const s = STATUS[o.status] || STATUS.pending;
                return (
                  <tr
                    key={o.id}
                    onClick={() => openDetail(o)}
                    data-testid={`order-row-${o.id}`}
                    className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer"
                  >
                    <td className="px-6 py-4 font-mono-data text-xs text-[#0A0A0A]">{o.order_number}</td>
                    <td className="px-6 py-4 text-sm text-[#0A0A0A] font-medium">{o.customer_name}</td>
                    <td className="px-6 py-4 text-xs text-[#4B5563]">{o.items.length}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${s.cls}`}>
                        {s.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-mono-data text-xs text-[#4B5563]">{fmtDate(o.created_at)}</td>
                    <td className="px-6 py-4 font-mono-data text-xs text-[#4B5563]">{o.delivery_date || "—"}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/lavorazione/${o.id}`;
                        }}
                        data-testid={`workflow-${o.id}`}
                        className="bg-[#2A9D8F] hover:bg-[#21867A] text-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider"
                        title="Visualizza piano di lavorazione passo-passo"
                      >
                        Lavorazione
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* New / edit order dialog */}
      <Dialog
        open={creating}
        onOpenChange={(v) => {
          setCreating(v);
          if (!v) {
            setEditingId(null);
            setForm(empty());
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editingId ? "Modifica Ordine" : "Nuovo Ordine Cliente"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Cliente *</label>
                <input required data-testid="customer-name-input"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                  value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Email</label>
                <input type="email" data-testid="customer-email-input"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                  value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Indirizzo di consegna</label>
                <input data-testid="customer-address-input"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                  value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Data consegna</label>
                <input type="date" data-testid="delivery-date-input"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                  value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563]">Righe ordine (Prodotti Finiti)</label>
                <button type="button" onClick={addItem} data-testid="add-order-item-btn"
                  className="text-xs font-display font-semibold uppercase tracking-wider text-[#1D3557] hover:text-[#2A4B7C] flex items-center gap-1">
                  <Plus size={12} weight="bold" /> Aggiungi
                </button>
              </div>
              {form.items.length === 0 && (
                <div className="bg-[#F9FAFB] border border-dashed border-[#E5E7EB] p-4 text-xs text-[#4B5563] text-center">
                  Aggiungi almeno una riga.
                </div>
              )}
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`order-item-${i}`}>
                    <div className="flex-1">
                      <Select value={it.product_id} onValueChange={(v) => updateItem(i, "product_id", v)}>
                        <SelectTrigger className="rounded-none">
                          <SelectValue placeholder="Seleziona prodotto finito" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} (stock: {p.stock} {p.unit})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <input type="number" step="1" min="1" placeholder="Qtà"
                      className="w-24 border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                      value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} />
                    <button type="button" onClick={() => removeItem(i)} className="text-[#4B5563] hover:text-[#E63946] p-2">
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">Note</label>
              <textarea rows={2}
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            <DialogFooter>
              <button type="button" onClick={() => { setCreating(false); setEditingId(null); setForm(empty()); }}
                className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]">
                Annulla
              </button>
              <button type="submit" data-testid="submit-order-btn"
                className="bg-[#1D3557] hover:bg-[#2A4B7C] text-white px-5 py-2.5 text-xs font-display font-semibold uppercase tracking-wider">
                {editingId ? "Salva Modifiche" : "Crea Ordine"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && (setDetail(null), setPlan(null))}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="order-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">
                  {detail.order_number} · {detail.customer_name}
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 text-sm bg-[#F9FAFB] border border-[#E5E7EB] p-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563]">Cliente</div>
                  <div className="font-medium text-[#0A0A0A]">{detail.customer_name}</div>
                  <div className="text-xs text-[#4B5563]">{detail.customer_email || "—"}</div>
                  <div className="text-xs text-[#4B5563]">{detail.customer_address || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563]">Stato</div>
                  <div>
                    <span className={`inline-block px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${(STATUS[detail.status] || STATUS.pending).cls}`}>
                      {(STATUS[detail.status] || STATUS.pending).label}
                    </span>
                  </div>
                  <div className="text-xs text-[#4B5563] mt-2">Consegna: {detail.delivery_date || "—"}</div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-2">Righe ordine</div>
                <table className="w-full bg-white border border-[#E5E7EB]">
                  <thead className="bg-[#F3F4F6]">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                      <th className="px-3 py-2 font-semibold">Prodotto</th>
                      <th className="px-3 py-2 font-semibold">EAN-13</th>
                      <th className="px-3 py-2 font-semibold text-right">Quantità</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.items.map((it, i) => (
                      <tr key={i} className="border-t border-[#E5E7EB]">
                        <td className="px-3 py-2 text-sm text-[#0A0A0A]">{it.product_name}</td>
                        <td className="px-3 py-2 font-mono-data text-xs text-[#4B5563]">{it.product_barcode}</td>
                        <td className="px-3 py-2 text-right font-mono-data text-sm font-semibold">{it.quantity} {it.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={computePlan} disabled={planLoading || detail.status === "cancelled"} data-testid="compute-order-plan-btn"
                  className="bg-[#1D3557] hover:bg-[#2A4B7C] text-white px-5 py-2 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50">
                  <ClipboardText size={14} weight="bold" />
                  {planLoading ? "Calcolo…" : "Calcola Piano"}
                </button>

                {plan && plan.can_execute && ["pending", "planned"].includes(detail.status) && (
                  <button
                    onClick={executeOrder}
                    disabled={actionLoading}
                    data-testid="execute-order-btn"
                    className="bg-[#2A9D8F] hover:bg-[#21867A] text-white px-5 py-2 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
                  >
                    <Play size={14} weight="fill" />
                    {actionLoading ? "Esecuzione…" : "Avvia Produzione"}
                  </button>
                )}

                {["pending", "planned"].includes(detail.status) && (
                  <button
                    onClick={() => { openEdit(detail); setDetail(null); setPlan(null); }}
                    data-testid="edit-order-btn"
                    className="border border-[#1D3557] text-[#1D3557] hover:bg-[#F3F4F6] px-4 py-2 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2"
                  >
                    <PencilSimple size={14} weight="bold" /> Modifica
                  </button>
                )}

                {detail.status === "cancelled" && (
                  <button
                    onClick={reopenOrder}
                    data-testid="reopen-order-btn"
                    className="bg-[#F4A261] hover:bg-[#E38A44] text-white px-5 py-2 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2"
                  >
                    ↺ Riattiva Ordine
                  </button>
                )}

                {!["shipped", "cancelled"].includes(detail.status) && (
                  <button onClick={cancelOrder}
                    className="border border-[#E63946] text-[#E63946] hover:bg-[#FBEDEE] px-4 py-2 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2">
                    <XCircle size={14} weight="bold" /> Annulla
                  </button>
                )}

                {["in_production", "ready", "planned", "pending"].includes(detail.status) && (
                  <button onClick={shipOrder} disabled={actionLoading}
                    className="ml-auto bg-[#457B9D] hover:bg-[#2A4B7C] text-white px-5 py-2 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50">
                    <Truck size={14} weight="bold" /> Spedisci
                  </button>
                )}
              </div>

              {plan && (
                <div className="mt-6 space-y-4">
                  {/* Items summary */}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-2">
                      Analisi righe (Stock vs Da Produrre)
                    </div>
                    <table className="w-full bg-white border border-[#E5E7EB]" data-testid="items-summary-table">
                      <thead className="bg-[#F3F4F6]">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                          <th className="px-3 py-2">Prodotto</th>
                          <th className="px-3 py-2 text-right">Richiesto</th>
                          <th className="px-3 py-2 text-right">Stock</th>
                          <th className="px-3 py-2 text-right">Da Produrre</th>
                          <th className="px-3 py-2">Distinta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plan.items_summary.map((s, i) => (
                          <tr key={i} className="border-t border-[#E5E7EB]">
                            <td className="px-3 py-2 text-sm">{s.product_name}</td>
                            <td className="px-3 py-2 text-right font-mono-data text-sm font-semibold">{s.requested} {s.unit}</td>
                            <td className="px-3 py-2 text-right font-mono-data text-sm text-[#4B5563]">{s.in_stock_before} {s.unit}</td>
                            <td className={`px-3 py-2 text-right font-mono-data text-sm font-semibold ${s.to_produce > 0 ? "text-[#F4A261]" : "text-[#2A9D8F]"}`}>
                              {s.to_produce} {s.unit}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {s.has_formula ? (
                                <span className="text-[#2A9D8F] flex items-center gap-1"><CheckCircle size={12} weight="fill" /> sì</span>
                              ) : (
                                <span className="text-[#E63946] flex items-center gap-1"><Warning size={12} weight="bold" /> no</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Missing materials */}
                  {plan.missing_materials.length > 0 && (
                    <div className="bg-[#FBEDEE] border border-[#E63946]/30 p-4" data-testid="missing-materials">
                      <div className="flex items-center gap-2 mb-2">
                        <Warning size={16} weight="bold" color="#E63946" />
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#E63946]">
                          Bloccanti per la produzione
                        </div>
                      </div>
                      {(() => {
                        const noFormulaPFs = plan.missing_materials.filter(
                          (l) => l.product_type === "prodotto_finito"
                        );
                        const realMissing = plan.missing_materials.filter(
                          (l) => l.product_type !== "prodotto_finito"
                        );
                        return (
                          <div className="space-y-3">
                            {noFormulaPFs.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#0A0A0A] mb-1">
                                  Prodotti finiti senza distinta base ({noFormulaPFs.length})
                                </div>
                                <ul className="space-y-1 ml-1">
                                  {noFormulaPFs.map((l) => (
                                    <li key={l.product_id} className="text-xs text-[#0A0A0A]">
                                      • <b>{l.product_name}</b>{" "}
                                      <span className="font-mono-data text-[#4B5563]">{l.product_barcode}</span>{" "}
                                      → richiede {l.shortage.toFixed(2)} {l.unit} ma{" "}
                                      <span className="text-[#E63946]">non ha una distinta</span>. Crea la formula
                                      in <i>Distinte Base</i> per produrlo automaticamente.
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {realMissing.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#0A0A0A] mb-1">
                                  Materiali da riordinare ({realMissing.length})
                                </div>
                                <ul className="space-y-1 ml-1">
                                  {realMissing.map((l) => (
                                    <li key={l.product_id} className="flex justify-between text-xs">
                                      <span className="text-[#0A0A0A]">
                                        [{l.product_type.toUpperCase()}] {l.product_name}{" "}
                                        <span className="font-mono-data text-[#4B5563]">{l.product_barcode}</span>
                                      </span>
                                      <span className="font-mono-data text-[#E63946]">
                                        disponibile {l.available} · mancano {l.shortage.toFixed(2)} {l.unit}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Production plan */}
                  {plan.production_plan.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-2">
                        Sequenza Produzione ({plan.production_plan.length} batch)
                      </div>
                      <div className="bg-white border border-[#E5E7EB]" data-testid="order-plan-list">
                        {plan.production_plan.map((step, i) => {
                          const isFinal = i === plan.production_plan.length - 1;
                          return (
                            <div key={i} className="flex items-center gap-4 p-3 border-b border-[#E5E7EB] last:border-b-0">
                              <div className={`w-8 h-8 flex items-center justify-center font-mono-data text-xs font-bold ${isFinal ? "bg-[#2A9D8F] text-white" : "bg-[#1D3557] text-white"}`}>
                                {i + 1}
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-display font-semibold">{step.formula_name}</div>
                                <div className="text-xs text-[#4B5563]">→ {step.output_product_name} · ×{step.multiplier} batch</div>
                              </div>
                              <div className="font-mono-data text-sm font-semibold">{step.planned_output.toFixed(2)} {step.output_unit}</div>
                            </div>
                          );
                        })}
                      </div>
                      {plan.can_execute && detail.status === "pending" && (
                        <div className="mt-4 bg-[#E6F4F1] border border-[#2A9D8F]/30 p-3 text-xs text-[#0A0A0A] flex items-center gap-2">
                          <CheckCircle size={16} weight="fill" color="#2A9D8F" />
                          Piano eseguibile · usa il pulsante <b>"Avvia Produzione"</b> in alto.
                        </div>
                      )}
                    </div>
                  )}

                  {plan.production_plan.length === 0 && plan.missing_materials.length === 0 && (
                    <div className="bg-[#E6F4F1] border border-[#2A9D8F]/30 p-4 flex items-center gap-3">
                      <CheckCircle size={20} weight="fill" color="#2A9D8F" />
                      <div className="text-sm text-[#0A0A0A]">
                        Tutti i prodotti sono disponibili a magazzino. Puoi <b>spedire</b> direttamente.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
